import React, { useState, useEffect } from 'react';
import { Fab, Dialog, Box, CircularProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UsageLimitCard from '../../common/UsageLimitCard';
import axios from 'axios';
import Cookies from 'js-cookie'; // or your cookie utility
import { getPlanLimit } from '../../../utils/planUtils';

/**
 * FloatingUsageAnalytics - FAB that fetches and displays usage snapshot in a modal (matches Dashboard logic)
 */
const FloatingUsageAnalytics = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentUsage, setCurrentUsage] = useState(0);
    // default to null so planUtils normalization can pick sensible fallback
    const [planType, setPlanType] = useState(null);

    useEffect(() => {
        if (!open) return;
        let isMounted = true;
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // exclusive
                const tenantId = Cookies.get('tenantId');
                if (!tenantId) {
                    if (isMounted) {
                        setError('Unable to load usage: Tenant ID not found.');
                        setLoading(false);
                    }
                    return;
                }
                const req = {
                    tenantId, // <-- add this line
                    agentId: '',
                    startDate: start.toISOString().slice(0, 10),
                    endDate: end.toISOString().slice(0, 10)
                };
                const [overviewRes, planRes] = await Promise.all([
                    axios.post('/analytics/overview', req),
                    axios.get('/account/plan')
                ]);
                const chats = overviewRes.data.chats || [];
                const totalChats = chats.reduce((sum, d) => sum + (d.count || 0), 0);
                // Accept whatever the backend returns (letter or name). Let planUtils normalize it.
                const plan = planRes?.data?.plan ?? planRes?.data?.planType ?? 'B';
                console.log('[FloatingUsageAnalytics] fetched plan billing cycle ashish:', planRes?.data.billing_cycle);
                if (isMounted) {
                    setPlanType(plan);
                    setCurrentUsage(totalChats);
                }
            } catch (e) {
                if (isMounted) setError('Failed to load usage data');
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [open]);

    // Determine FAB color based on usage percentage
    let usagePercent = 0;
    const limit = getPlanLimit(planType); // <-- use utility
    if (planType && currentUsage && limit) {
        usagePercent = (currentUsage / limit) * 100;
    }
    let fabColor = '#43a047'; // green (good)
    let fabHover = '#388e3c';
    if (usagePercent >= 90) {
        fabColor = '#d32f2f'; // red
        fabHover = '#b71c1c';
    } else if (usagePercent >= 75) {
        fabColor = '#fbc02d'; // yellow
        fabHover = '#f9a825';
    }
    return (
        <>
            <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1300 }}>
                <Tooltip title="Click to view your usage data" arrow>
                    <Fab
                        size="medium"
                        aria-label="usage snapshot"
                        onClick={() => setOpen(true)}
                        sx={{ backgroundColor: fabColor, color: '#fff', '&:hover': { backgroundColor: fabHover } }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 32 }} />
                    </Fab>
                </Tooltip>
            </Box>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 3, minWidth: 320, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : error ? (
                        <Box sx={{ textAlign: 'center' }}>{error}</Box>
                    ) : (
                        <UsageLimitCard
                            currentUsage={currentUsage}
                            planType={planType}
                            onUpgrade={() => window.location.assign('/account-settings')}
                        />
                    )}
                </Box>
            </Dialog>
        </>
    );
};

export default FloatingUsageAnalytics;
